import React, { useEffect, useState } from "react";
import {
    Database, Mail, PhoneCall, Users, Megaphone, Globe2,
    Filter, CheckCircle2,
} from "lucide-react";
import CTAButton from "@/components/CTAButton";
import Reveal from "@/components/Reveal";
import { LINKS, IMAGES, SERVICE_MEDIA, FLAG } from "@/lib/content";
import { useT } from "@/lib/i18n";

const SECTION_KEYS = ["cegadatbazis", "email", "phone", "staff", "marketing", "expansion"];
const SECTION_IDS = {
    cegadatbazis: "cegadatbazis",
    email: "email-marketing",
    phone: "telefonos-megkereses",
    staff: "munkatars-biztositasa",
    marketing: "marketing",
    expansion: "terjeszkedes",
};
const sectionIcons = {
    cegadatbazis: Database, email: Mail, phone: PhoneCall,
    staff: Users, marketing: Megaphone, expansion: Globe2,
};
const countryFlag = (name) => {
    const map = {
        "Magyarország": "🇭🇺", "Hungary": "🇭🇺", "Maďarsko": "🇭🇺", "Ungarn": "🇭🇺",
        "Szlovákia": "🇸🇰", "Slovakia": "🇸🇰", "Slovensko": "🇸🇰", "Slowakei": "🇸🇰",
        "Ausztria": "🇦🇹", "Austria": "🇦🇹", "Rakúsko": "🇦🇹", "Österreich": "🇦🇹",
        "Csehország": "🇨🇿", "Czechia": "🇨🇿", "Česko": "🇨🇿", "Tschechien": "🇨🇿",
    };
    return map[name] || "🏳️";
};

function StickyNav({ activeId, sections, sideNavTitle }) {
    return (
        <aside className="hidden lg:block sticky top-28 self-start" data-testid="services-side-nav">
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">
                {sideNavTitle}
            </div>
            <ul className="space-y-1">
                {sections.map((s) => {
                    const active = activeId === s.id;
                    return (
                        <li key={s.id}>
                            <a href={`#${s.id}`}
                                className={`group flex items-center gap-3 py-2 pl-3 border-l text-sm transition-colors ${active ? "border-[color:var(--accent)] text-white" : "border-white/10 text-white/55 hover:text-white hover:border-white/30"}`}
                                data-testid={`side-nav-${s.id}`}>
                                <span>{s.label}</span>
                            </a>
                        </li>
                    );
                })}
            </ul>
        </aside>
    );
}

function ServiceMedia({ mediaKey, label }) {
    const m = SERVICE_MEDIA[mediaKey];
    if (!m) return null;
    return (
        <div className="relative overflow-hidden rounded-lg border border-white/10 bg-black shadow-[0_20px_60px_-20px_rgba(37,99,235,0.35)] aspect-[4/5] md:aspect-[3/4]" data-testid={`service-media-${mediaKey}`}>
            <video className="absolute inset-0 w-full h-full object-cover"
                src={m.video} poster={m.poster}
                autoPlay muted loop playsInline preload="metadata" aria-hidden="true" />
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-[10px] font-semibold tracking-widest uppercase text-white/85 border border-white/10">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" />
                {label}
            </div>
        </div>
    );
}

function TopChipsNav({ sections }) {
    return (
        <div className="lg:hidden overflow-x-auto no-scrollbar -mx-5 md:-mx-10">
            <div className="flex gap-2 px-5 md:px-10 pb-2">
                {sections.map((s) => (
                    <a key={s.id} href={`#${s.id}`}
                        className="whitespace-nowrap text-sm px-3.5 py-2 rounded-full border border-white/12 text-white/80 hover:text-white hover:border-white/30 transition-colors"
                        data-testid={`chip-nav-${s.id}`}>
                        {s.label}
                    </a>
                ))}
            </div>
        </div>
    );
}

function SectionHeader({ eyebrow, title, icon: Icon }) {
    return (
        <Reveal>
            <div className="flex items-center gap-3 mb-6">
                <Icon size={22} strokeWidth={1.5} className="text-[color:var(--accent)]" />
                <span className="eyebrow">{eyebrow}</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">{title}</h2>
        </Reveal>
    );
}

function BottomCta({ t, sectionKey }) {
    return (
        <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col sm:flex-row gap-3">
                <CTAButton href={LINKS.consultation} external testId={`${sectionKey}-cta-consultation`}>
                    {t.services.ctaConsultation}
                </CTAButton>
                <a href={LINKS.contact} className="btn-secondary" data-testid={`${sectionKey}-cta-contact`}>
                    {t.services.ctaContact}
                </a>
            </div>
        </Reveal>
    );
}

function DatabaseSection({ t }) {
    const s = t.services.sections.cegadatbazis;
    return (
        <section id={SECTION_IDS.cegadatbazis} className="anchor-offset py-16 md:py-24 border-b border-white/8" data-testid={`service-section-${SECTION_IDS.cegadatbazis}`}>
            <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center mb-10 md:mb-14">
                <div className="md:col-span-7 order-2 md:order-1">
                    <SectionHeader eyebrow={t.services.eyebrows.dataDriven} title={s.title} icon={Database} />
                </div>
                <div className="md:col-span-5 order-1 md:order-2">
                    <Reveal delay={0.08}><ServiceMedia mediaKey="cegadatbazis" label={s.label} /></Reveal>
                </div>
            </div>

            <div className="grid md:grid-cols-12 gap-10">
                <div className="md:col-span-7 space-y-4">
                    {s.intro.map((p, i) => (
                        <Reveal key={i} delay={i * 0.05}>
                            <p className="text-white/70 leading-relaxed text-[1.02rem]">{p}</p>
                        </Reveal>
                    ))}
                </div>
                <div className="md:col-span-5">
                    <Reveal>
                        <div className="p-6 rounded-md border border-white/10 bg-white/[0.02]">
                            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-widest mb-4">
                                <Filter size={14} strokeWidth={1.5} />
                                {s.filtersTitle}
                            </div>
                            <ul className="grid grid-cols-2 gap-2">
                                {s.filters.map((f) => (
                                    <li key={f} className="flex items-center gap-2 text-white/85 text-sm">
                                        <span className="w-1 h-1 rounded-full bg-[color:var(--accent)]" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                {s.stats.map((st, i) => (
                    <Reveal key={st.country} delay={i * 0.05}>
                        <div className="p-6 rounded-md border border-white/10 bg-[color:var(--bg-surface)]/60" data-testid={`db-stat-${i}`}>
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-2xl" aria-hidden="true">{countryFlag(st.country)}</span>
                                <span className="text-[10px] text-white/40 uppercase tracking-widest">{s.statLabel}</span>
                            </div>
                            <div className="font-display text-3xl md:text-4xl text-white tracking-tight">{st.value}</div>
                            <div className="mt-1 text-white/55 text-sm">{st.country}</div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <BottomCta t={t} sectionKey={SECTION_IDS.cegadatbazis} />
        </section>
    );
}

function StepsSection({ t, sectionKey, mediaLeft = false }) {
    const s = t.services.sections[sectionKey];
    const Icon = sectionIcons[sectionKey];
    const id = SECTION_IDS[sectionKey];
    return (
        <section id={id} className="anchor-offset py-16 md:py-24 border-b border-white/8" data-testid={`service-section-${id}`}>
            <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center mb-10 md:mb-14">
                <div className={`md:col-span-7 order-2 ${mediaLeft ? "md:order-2" : "md:order-1"}`}>
                    <SectionHeader eyebrow={t.services.eyebrows.process} title={s.title} icon={Icon} />
                </div>
                <div className={`md:col-span-5 order-1 ${mediaLeft ? "md:order-1" : "md:order-2"}`}>
                    <Reveal delay={0.08}><ServiceMedia mediaKey={sectionKey} label={s.label} /></Reveal>
                </div>
            </div>

            <ol className="relative border-l border-white/10 pl-8 md:pl-12 space-y-8">
                {s.steps.map((step, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <li className="relative">
                            <span className="absolute -left-[41px] md:-left-[57px] top-1 flex items-center justify-center w-8 h-8 rounded-full bg-[color:var(--bg-surface)] border border-white/15 text-[color:var(--accent)] font-display text-sm">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <h3 className="font-display text-lg md:text-xl text-white leading-snug">{step.title}</h3>
                            <p className="mt-2 text-white/65 leading-relaxed text-[0.98rem] max-w-3xl">{step.text}</p>
                        </li>
                    </Reveal>
                ))}
            </ol>

            <BottomCta t={t} sectionKey={id} />
        </section>
    );
}

function ItemsSection({ t, sectionKey, mediaLeft = false }) {
    const s = t.services.sections[sectionKey];
    const Icon = sectionIcons[sectionKey];
    const id = SECTION_IDS[sectionKey];
    return (
        <section id={id} className="anchor-offset py-16 md:py-24 border-b border-white/8" data-testid={`service-section-${id}`}>
            <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center mb-10 md:mb-14">
                <div className={`md:col-span-7 order-2 ${mediaLeft ? "md:order-2" : "md:order-1"}`}>
                    <SectionHeader eyebrow={t.services.eyebrows.service} title={s.title} icon={Icon} />
                </div>
                <div className={`md:col-span-5 order-1 ${mediaLeft ? "md:order-1" : "md:order-2"}`}>
                    <Reveal delay={0.08}><ServiceMedia mediaKey={sectionKey} label={s.label} /></Reveal>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                {s.items.map((it, i) => (
                    <Reveal key={i} delay={i * 0.04}>
                        <div className="py-5 border-b border-white/8 flex gap-4">
                            <CheckCircle2 size={20} strokeWidth={1.5} className="mt-1 shrink-0 text-[color:var(--accent)]" />
                            <div>
                                <h3 className="text-white font-display text-lg leading-snug">{it.title}</h3>
                                <p className="mt-1.5 text-white/65 text-[0.98rem] leading-relaxed">{it.text}</p>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <BottomCta t={t} sectionKey={id} />
        </section>
    );
}

export default function Services() {
    const { t } = useT();
    const [activeId, setActiveId] = useState(SECTION_IDS.cegadatbazis);

    const sections = SECTION_KEYS.map((k) => ({
        id: SECTION_IDS[k], key: k, label: t.services.sections[k].label,
    }));

    useEffect(() => {
        const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id); });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
        );
        els.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t.code]);

    return (
        <main className="pt-[72px] md:pt-[80px]" data-testid="services-page">
            <section className="relative overflow-hidden" data-testid="services-hero">
                <div className="absolute inset-0 -z-10">
                    <video className="w-full h-full object-cover object-center opacity-35"
                        src={IMAGES.heroServicesVideo} poster={IMAGES.heroServices}
                        autoPlay muted loop playsInline preload="metadata" aria-hidden="true" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bg-base)]/70 via-[color:var(--bg-base)]/85 to-[color:var(--bg-base)]" />
                    <div className="absolute inset-0 bg-grid opacity-40" />
                </div>
                <div className="container-x pt-24 md:pt-32 pb-16 md:pb-20">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-8">
                            <span className="w-8 h-px bg-[color:var(--accent)]" />
                            <span className="eyebrow">{t.services.hero.eyebrow}</span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-white max-w-5xl" data-testid="services-heading">
                            {t.services.hero.headingA}
                            <br />
                            <span className="text-[color:var(--accent)]">{t.services.hero.headingB}</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-white/70 text-lg max-w-2xl leading-relaxed">{t.services.hero.subheading}</p>
                    </Reveal>
                </div>
            </section>

            <div className="container-x pb-4 md:pb-6 lg:hidden">
                <TopChipsNav sections={sections} />
            </div>

            <div className="container-x">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-3">
                        <StickyNav activeId={activeId} sections={sections} sideNavTitle={t.services.sideNavTitle} />
                    </div>
                    <div className="lg:col-span-9">
                        <DatabaseSection t={t} />
                        <StepsSection t={t} sectionKey="email" mediaLeft={true} />
                        <ItemsSection t={t} sectionKey="phone" mediaLeft={false} />
                        <ItemsSection t={t} sectionKey="staff" mediaLeft={true} />
                        <ItemsSection t={t} sectionKey="marketing" mediaLeft={false} />
                        <StepsSection t={t} sectionKey="expansion" mediaLeft={true} />
                    </div>
                </div>
            </div>

            <section className="section" data-testid="services-bottom-cta">
                <div className="container-x">
                    <Reveal>
                        <div className="p-8 md:p-14 rounded-lg border border-white/10 bg-[color:var(--bg-surface)]">
                            <div className="grid md:grid-cols-12 gap-8 items-center">
                                <div className="md:col-span-8">
                                    <p className="eyebrow">{t.services.bottomCta.eyebrow}</p>
                                    <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight text-white leading-tight">
                                        {t.services.bottomCta.heading}
                                    </h2>
                                    <p className="mt-4 text-white/60 leading-relaxed max-w-2xl">
                                        {t.services.bottomCta.subheading}
                                    </p>
                                </div>
                                <div className="md:col-span-4 md:text-right">
                                    <div className="flex flex-col sm:flex-row md:justify-end gap-3">
                                        <CTAButton href={LINKS.consultation} external testId="services-bottom-cta-primary">
                                            {t.services.ctaConsultation}
                                        </CTAButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
